import { forwardRef, useMemo, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { Menu } from '@material-ui/core';
import cx from 'classnames';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';

import { UploadResourceFileHelper } from 'src/components/UploadResourceFileHelper';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { useFreshRef } from 'src/lib/hooks/useFreshRef';
import { useKeyHandlers } from 'src/lib/hooks/useKeyHandlers';

import { MenuContentAll } from './components/MenuContentAll';
import { MenuContentBank } from './components/MenuContentBank';
import { MenuContentDefault } from './components/MenuContentDefault';
import { MenuContentImage } from './components/MenuContentImage';
import { MenuContentResource } from './components/MenuContentResource';
import { MenuContentUrl } from './components/MenuContentUrl';
import { MenuExternalChannel } from './components/MenuExternalChannel';
import { MenuHeader } from './components/MenuHeader';
import { useSupportKeyboardFocus } from './lib/useSupportKeyboardFocus';
import { PROMPT_MENU_TYPE } from './lib';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool.isRequired,
  menuAnchor: PropTypes.shape(),
  menuProps: PropTypes.shape(),
  onCloseMenu: PropTypes.func.isRequired,
  menuType: PropTypes.string,
  setMenuType: PropTypes.func.isRequired,
  availableResources: PropTypes.arrayOf(PropTypes.shape()),
  brandBriefsMenuItems: PropTypes.arrayOf(PropTypes.shape()),
  attachedResources: PropTypes.arrayOf(PropTypes.shape()),
  index: PropTypes.number,
  onAction: PropTypes.func.isRequired,
  externalChannelMenuProps: PropTypes.shape({
    onConnectExternalChannel: PropTypes.func.isRequired,
    onCheckExternalChannelConnected: PropTypes.func.isRequired,
  }),
  quickSearchMode: PropTypes.bool,
  searchTerm: PropTypes.string,
  isApplicationModeInfinity: PropTypes.bool,
  noTags: PropTypes.bool,
  hideImages: PropTypes.bool,
  displayAlternativeTitle: PropTypes.bool,
  shouldIncludeAudioVideoResources: PropTypes.bool,
};

const defaultProps = {
  className: null,
  menuAnchor: null,
  menuProps: {},
  menuType: '',
  availableResources: [],
  brandBriefsMenuItems: [],
  attachedResources: [],
  index: 0,
  externalChannelMenuProps: null,
  isApplicationModeInfinity: false,
  noTags: false,
  hideImages: false,
  quickSearchMode: false,
  searchTerm: null,
  displayAlternativeTitle: false,
  shouldIncludeAudioVideoResources: false,
};

const EMPTY_ARRAY = [];

export const PromptMenu = forwardRef(
  (
    {
      className,
      open,
      menuAnchor,
      menuProps,
      onCloseMenu,
      // TODO: convert to `initialMenuType` and remove `setMenuType`
      menuType,
      setMenuType,
      availableResources,
      brandBriefsMenuItems,
      attachedResources,
      index,
      onAction,
      externalChannelMenuProps,
      quickSearchMode,
      searchTerm,
      isApplicationModeInfinity,
      noTags,
      hideImages,
      displayAlternativeTitle,
      shouldIncludeAudioVideoResources,
    },
    forwardedRef
  ) => {
    const styles = useStyles();
    const rootRef = useRef(null);
    const uploadFileHelperRef = useRef(null);
    const attachedResourcesRef = useFreshRef(attachedResources);

    useSupportKeyboardFocus({ rootRef, open, menuType });

    const groupedResources = useMemo(
      () => groupBy(availableResources, 'contentType'),
      [availableResources]
    );

    const { handleCloseMenu, handleUploadFile, handleSearchFocus, handleMenuCardClick } =
      useHandlers({
        availableResources,
        setMenuType,
        onCloseMenu,
        index,
        onConnectExternalChannel: externalChannelMenuProps?.onConnectExternalChannel,
        onAction,
        uploadFileHelperRef,
        attachedResourcesRef,
      });

    useKeyHandlers({ Escape: handleCloseMenu });

    return (
      <Menu
        classes={{
          paper: cx(styles.menuPaper, className),
          list: styles.menuList,
        }}
        ref={mergeRefs([rootRef, forwardedRef])}
        open={open}
        anchorEl={menuAnchor}
        onClose={handleCloseMenu}
        getContentAnchorEl={null}
        variant="menu"
        data-testid="PromptMenu"
        {...menuProps}
      >
        {!quickSearchMode && (
          <MenuHeader
            menuType={menuType}
            onChangeMenuType={setMenuType}
            onCloseMenu={handleCloseMenu}
            displayAlternativeTitle={displayAlternativeTitle}
          />
        )}

        <div
          className={styles.buttonsWrapper}
          data-testid="promptInstructionsMenuContent"
        >
          {menuType === PROMPT_MENU_TYPE.default && (
            <MenuContentDefault
              onMenuCardClick={handleMenuCardClick}
              onSearchFocus={handleSearchFocus}
              externalChannelMenuProps={externalChannelMenuProps}
              hideImages={hideImages}
              shouldIncludeAudioVideoResources={shouldIncludeAudioVideoResources}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.all && (
            <MenuContentAll
              resources={availableResources || EMPTY_ARRAY}
              bankResources={brandBriefsMenuItems || EMPTY_ARRAY}
              onAction={onAction}
              isApplicationModeInfinity={isApplicationModeInfinity}
              quickSearchMode={quickSearchMode}
              noTags={noTags}
              shouldIncludeAudioVideoResources={shouldIncludeAudioVideoResources}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.image && (
            <MenuContentImage
              imageResources={groupedResources[RESOURCE_CONTENT_TYPE.image] || EMPTY_ARRAY}
              onAction={onAction}
              onUploadClick={() => handleUploadFile(RESOURCE_CONTENT_TYPE.image)}
              quickSearchMode={quickSearchMode}
              searchTerm={searchTerm}
              noTags={noTags}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.bank && (
            <MenuContentBank
              bankResources={brandBriefsMenuItems}
              onAction={onAction}
              isApplicationModeInfinity={isApplicationModeInfinity}
              quickSearchMode={quickSearchMode}
              noTags={noTags}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.url && (
            <MenuContentUrl
              urlResources={groupedResources[RESOURCE_CONTENT_TYPE.url] || EMPTY_ARRAY}
              onAction={onAction}
              quickSearchMode={quickSearchMode}
              noTags={noTags}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.document && (
            <MenuContentResource
              resourceType={RESOURCE_CONTENT_TYPE.document}
              resources={groupedResources[RESOURCE_CONTENT_TYPE.document] || EMPTY_ARRAY}
              onAction={onAction}
              onUploadClick={() => handleUploadFile(RESOURCE_CONTENT_TYPE.document)}
              quickSearchMode={quickSearchMode}
              menuType={menuType}
              noTags={noTags}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.audio && (
            <MenuContentResource
              resourceType={RESOURCE_CONTENT_TYPE.audio}
              resources={groupedResources[RESOURCE_CONTENT_TYPE.audio] || EMPTY_ARRAY}
              onAction={onAction}
              onUploadClick={() => handleUploadFile(RESOURCE_CONTENT_TYPE.audio)}
              quickSearchMode={quickSearchMode}
              menuType={menuType}
              noTags={noTags}
            />
          )}

          {menuType === PROMPT_MENU_TYPE.video && (
            <MenuContentResource
              resourceType={RESOURCE_CONTENT_TYPE.video}
              resources={groupedResources[RESOURCE_CONTENT_TYPE.video] || EMPTY_ARRAY}
              onAction={onAction}
              onUploadClick={() => handleUploadFile(RESOURCE_CONTENT_TYPE.video)}
              quickSearchMode={quickSearchMode}
              menuType={menuType}
              noTags={noTags}
            />
          )}

          {[PROMPT_MENU_TYPE.salesforce, PROMPT_MENU_TYPE.microsoftDynamicsSales].includes(
            menuType
          ) && (
            <MenuExternalChannel
              onAction={onAction}
              menuType={menuType}
              {...externalChannelMenuProps}
            />
          )}
        </div>

        <UploadResourceFileHelper ref={uploadFileHelperRef} />
      </Menu>
    );
  }
);

PromptMenu.displayName = 'PromptMenu';
PromptMenu.propTypes = propTypes;
PromptMenu.defaultProps = defaultProps;
